import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, AlertCircle, RotateCcw, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OtpInput } from "@/components/OtpInput";

// AUTH-02 — MFA verify (after sign-in challenge)
export default function MfaVerifyPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [backup, setBackup] = useState("");
  const otpRef = useRef(null);

  const codeReady = code.length === 6;

  const verify = (codeIn) => {
    if (codeIn === "000000") {
      setError("That code didn't work. Codes expire every 30 seconds.");
      otpRef.current?.reset();
      return;
    }
    navigate("/member/dashboard");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="size-10 rounded-md bg-brand-50 text-brand-700 grid place-items-center"><ShieldCheck className="size-5" /></div>
        <div>
          <div className="eyebrow text-brand-700">Two-factor</div>
          <h1 className="font-brand text-h1 text-ink">Enter your code</h1>
        </div>
      </div>

      <p className="text-body-sm text-ink-muted leading-relaxed">
        Open your authenticator app and enter the current 6-digit code for{" "}
        <span className="font-mono text-code text-ink">jennifer.walsh@gmail.com</span>.
      </p>

      {!useBackup ? (
        <>
          <OtpInput
            ref={otpRef}
            length={6}
            testidPrefix="mfa"
            onChange={(c) => { setCode(c); if (c.length > 0) setError(""); }}
            onComplete={verify}
          />

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-caption text-red-700">
              <AlertCircle className="size-3.5 mt-0.5" /><span dangerouslySetInnerHTML={{ __html: error }} />
            </div>
          )}

          <Button
            onClick={() => verify(code)}
            disabled={!codeReady}
            data-testid="member-mfa-verify-btn"
            className="w-full h-11 bg-brand-700 hover:bg-brand-800 text-white"
          >
            Verify <ArrowRight className="size-4 ml-2" />
          </Button>

          <div className="text-center text-caption text-ink-subtle space-x-3">
            <button onClick={() => setUseBackup(true)} className="text-brand-700 hover:text-brand-800 font-semibold">
              Use a recovery code
            </button>
            <span>·</span>
            <button onClick={() => { otpRef.current?.reset(); setError(""); }}
                    className="text-ink-muted hover:text-ink inline-flex items-center gap-1">
              <RotateCcw className="size-3" /> Reset
            </button>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          <p className="text-body-sm text-ink-muted">
            Enter one of the 12-character recovery codes you saved during setup. Each code works once.
          </p>
          <Input value={backup} onChange={(e) => setBackup(e.target.value.toUpperCase())}
                 placeholder="XXXX-XXXX-XXXX" className="font-mono tabular-nums text-center" data-testid="backup-code-input" />
          <Button onClick={() => navigate("/member/dashboard")}
                  disabled={backup.length < 12}
                  className="w-full h-11 bg-brand-700 hover:bg-brand-800 text-white">Verify recovery code</Button>
          <button onClick={() => setUseBackup(false)}
                  className="w-full text-caption text-ink-muted hover:text-ink">Back to authenticator code</button>
        </div>
      )}

      <div className="border-t border-line pt-4 text-center text-caption text-ink-subtle">
        Lost your authenticator?{" "}
        <Link to="/contact" className="text-brand-700 font-semibold hover:text-brand-800">Contact support</Link>
      </div>
    </div>
  );
}
